import api from './api';

/**
 * Serviço para gerenciar simulação de usuário (GOD only)
 *
 * Fluxo:
 * 1. startSimulation → chama API como GOD (notificação)
 * 2. AuthContext.enterSimulation → ajusta localStorage e state, busca /auth/me com x-simulated-user
 * 3. Backend auth middleware troca o usuário para o simulado
 * 4. stopSimulation → limpa localStorage PRIMEIRO → chama API como GOD (notificação)
 * 5. AuthContext.exitSimulation → restaura projeto original e busca /auth/me sem simulação
 */
class SimulationService {
  /**
   * Lista usuários disponíveis para simulação
   * IMPORTANTE: limpa simulação temporariamente para chamar como GOD
   */
  async listUsers(search = '', projectId = null) {
    this._clearHeaderTemporarily();
    try {
      const params = {};
      if (search) params.search = search;
      if (projectId) params.projectId = projectId;

      const response = await api.get('/simulation/users', { params });
      return response.data;
    } finally {
      this._restoreHeader();
    }
  }

  /**
   * Inicia simulação de um usuário no backend.
   * O gerenciamento de localStorage/state fica no AuthContext.enterSimulation.
   */
  async startSimulation(userId) {
    this._clearHeaderTemporarily();
    try {
      const response = await api.post('/simulation/start', { userId });
      return response.data;
    } finally {
      this._restoreHeader();
    }
  }

  /**
   * Para a simulação atual
   * Limpa localStorage PRIMEIRO para que a chamada API vá como GOD
   */
  async stopSimulation() {
    this.clearSimulation();
    try {
      await api.post('/simulation/stop');
    } catch (error) {
      console.error('Erro ao notificar backend sobre parada de simulação:', error);
    }
  }

  /**
   * Retorna dados da simulação armazenados localmente
   */
  getLocalSimulation() {
    const userId = localStorage.getItem('simulatedUserId');
    if (!userId) return null;

    try {
      const userInfo = JSON.parse(localStorage.getItem('simulatedUserInfo') || '{}');
      return { simulatedUserId: userId, ...userInfo };
    } catch (e) {
      return { simulatedUserId: userId, name: 'Usuário', email: '' };
    }
  }

  /**
   * Verifica se simulação está ativa
   */
  isSimulating() {
    return !!localStorage.getItem('simulatedUserId');
  }

  /**
   * Limpa todos os dados de simulação
   */
  clearSimulation() {
    delete api.defaults.headers.common['x-simulated-user'];
    localStorage.removeItem('simulatedUserId');
    localStorage.removeItem('simulatedUserInfo');
  }

  // --- Helpers internos ---

  _clearHeaderTemporarily() {
    this._savedSimulatedUserId = localStorage.getItem('simulatedUserId');
    if (this._savedSimulatedUserId) {
      localStorage.removeItem('simulatedUserId');
      delete api.defaults.headers.common['x-simulated-user'];
    }
  }

  _restoreHeader() {
    if (this._savedSimulatedUserId) {
      localStorage.setItem('simulatedUserId', this._savedSimulatedUserId);
      api.defaults.headers.common['x-simulated-user'] = this._savedSimulatedUserId;
      this._savedSimulatedUserId = null;
    }
  }
}

export default new SimulationService();
